// src/components/ValueFilterDropdown.jsx
//
// A multi-select over one column's values, used by every filter on the report
// setup screens. Shared rather than copied: the "All means no filter" rule
// below is subtle enough that two implementations of it would drift.
import React, { useEffect, useMemo, useRef, useState } from "react";

/* Takes whatever list it is handed — the pickers on both report screens are
   this same control over different columns.

   "All" is the absence of a filter, not one of the values: picking it clears
   the rest, picking a value clears it, and removing the last value falls back
   to All rather than leaving a filter that matches nothing. */
const ValueFilterDropdown = ({
  icon,
  label,
  /* Spelled out per filter rather than label + "s". Status would have read
     "All statuss", and Work "All works" when what it picks is work types. */
  plural,
  options,
  selected,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const isAll = selected.includes("All") || selected.length === 0;

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((o) => String(o).toLowerCase().includes(term));
  }, [options, search]);

  const toggle = (option) => {
    if (option === "All") {
      onChange(["All"]);
      return;
    }
    const current = selected.filter((v) => v !== "All");
    const next = current.includes(option)
      ? current.filter((v) => v !== option)
      : [...current, option];
    onChange(next.length ? next : ["All"]);
  };

  const allLabel = `All ${plural || `${label.toLowerCase()}s`}`;

  const text = isAll
    ? allLabel
    : selected.length === 1
    ? selected[0]
    : `${selected.length} selected`;

  return (
    <div ref={wrapperRef} className="position-relative w-100">
      <button
        type="button"
        className="btn btn-white border d-flex align-items-center justify-content-between shadow-sm w-100"
        style={{
          height: "40px",
          fontSize: "0.85rem",
          fontWeight: 600,
          color: "#495057",
          borderRadius: "8px",
          backgroundColor: "#fff",
        }}
        onClick={() => setIsOpen((o) => !o)}
      >
        <span className="d-flex align-items-center gap-2 text-truncate">
          <i className={`bi ${icon} text-primary opacity-75`} />
          <span className="text-truncate">{text}</span>
        </span>
        <i
          className="bi bi-chevron-down ms-2 text-muted"
          style={{ fontSize: "0.7rem" }}
        />
      </button>

      {isOpen && (
        <div
          className="shadow-lg border-0 rounded-3 bg-white py-2"
          style={{
            position: "absolute",
            top: "115%",
            left: 0,
            minWidth: "220px",
            width: "100%",
            maxHeight: "300px",
            overflowY: "auto",
            zIndex: 1000,
          }}
        >
          <div className="px-2 pb-2 sticky-top bg-white">
            <input
              autoFocus
              type="text"
              className="form-control form-control-sm"
              placeholder={`Search ${label.toLowerCase()}…`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{ fontSize: "0.8rem", height: "32px" }}
            />
          </div>

          <div
            className="px-3 py-2 d-flex align-items-center gap-2"
            style={{ cursor: "pointer" }}
            onClick={() => toggle("All")}
          >
            <input
              type="checkbox"
              className="form-check-input"
              checked={isAll}
              readOnly
            />
            <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>
              {allLabel}
            </span>
          </div>

          {visible.length === 0 && (
            <div className="px-3 py-2 text-muted" style={{ fontSize: "0.8rem" }}>
              Nothing to choose — no rows carry a {label.toLowerCase()}.
            </div>
          )}

          {visible.map((option) => (
            <div
              key={option}
              className="px-3 py-2 d-flex align-items-center gap-2"
              style={{ cursor: "pointer" }}
              onClick={() => toggle(option)}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#f8f9fa")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
            >
              <input
                type="checkbox"
                className="form-check-input"
                checked={!isAll && selected.includes(option)}
                readOnly
              />
              <span
                className="text-truncate"
                style={{ fontSize: "0.85rem", color: "#333" }}
              >
                {option}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ValueFilterDropdown;
